"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Input,
  P2,
  Tag,
  Text,
  TextLabel,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@focal/ui-kit";
import { z } from "zod";

import { Client, EmailAddress } from "@/types";
import { useCrm } from "@/components/pages/Settings/integrations/hooks";
import { useClientContacts } from "@/components/pages/Clients/components/hooks";
import { ContactListHandle } from "./types";

const emailSchema = z.string().trim().email("Enter a valid email address");

/** Maximum number of email addresses a client may carry (enforced client-side). */
const MAX_EMAILS = 5;

const PRIMARY_EMAIL_TOOLTIP =
  "This is the primary email that will be used by Focal for drafting emails, and identifying meeting participants from calendar event invitees.";

interface ClientEmailListProps {
  client: Client;
  /**
   * When true, the list is display-only: rows render as plain text with no
   * inputs or actions. Used on the client detail surface where modification
   * lives exclusively in the edit modal.
   */
  readOnly?: boolean;
}

/**
 * Renders a client's full set of (untyped) email addresses.
 *
 * In editable mode every row is an always-editable input. Typing only updates
 * local draft state — value edits are NOT persisted until the parent modal
 * calls {@link ContactListHandle.flushPendingEdits} (on its Save button). A
 * per-row 3-dot menu exposes the immediate actions (make-primary, delete) and
 * an inline "Add email" affordance persists new rows immediately.
 *
 * All mutations are wired through {@link useClientContacts}, which invalidates
 * the relevant query keys so the details header, this list, and the clients
 * table all reflect backend behaviour (primary-promotion-on-delete and the
 * `client.email` mirror writeback).
 *
 * When the client is CRM-synced and contact sync is enabled, value edits,
 * add, and delete are disabled (those belong in the CRM, mirroring the guards
 * in `ClientModal`). Promoting an existing email to primary is a benign local
 * re-ordering and stays available.
 */
export const ClientEmailList = forwardRef<
  ContactListHandle,
  ClientEmailListProps
>(({ client, readOnly = false }, ref) => {
  const { isContactSyncEnabled } = useCrm();
  const { addEmail, updateEmail, deleteEmail, setPrimaryEmail } =
    useClientContacts(client.id);

  // Disable mutating actions for CRM-synced clients when contact sync is on:
  // those edits must happen in the CRM, mirroring ClientModal's guards.
  const isSyncLocked = !!client.crmContact?.crmId && isContactSyncEnabled;

  const emails: EmailAddress[] = useMemo(
    () => client.emailAddresses ?? [],
    [client.emailAddresses],
  );

  // Preserve a stable display order while editing. Promoting a row to primary
  // re-sorts the API response (primary first), which would make the row jump to
  // the top. We render rows in the order they first appeared (new rows append at
  // the end), so only the "Primary" label moves. The read-only profile display
  // keeps the API's primary-first order, which is fine there.
  const orderRef = useRef<string[]>([]);
  const orderedEmails = useMemo(() => {
    const byId = new Map(emails.map((email) => [email.id, email]));
    const order = orderRef.current.filter((id) => byId.has(id));
    const known = new Set(order);
    for (const email of emails) if (!known.has(email.id)) order.push(email.id);
    orderRef.current = order;
    return order.map((id) => byId.get(id)!);
  }, [emails]);

  // Staged value edits, keyed by email id. A row is "dirty" when it has a draft
  // that differs from the persisted value. Drafts persist across the refetches
  // triggered by immediate actions (delete / make-primary / add) and are only
  // cleared once flushed or once the row disappears.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  // Drop drafts for rows that no longer exist (e.g. after a delete refetch).
  useEffect(() => {
    setDrafts((prev) => {
      const ids = new Set(emails.map((email) => email.id));
      const next: Record<string, string> = {};
      let changed = false;
      for (const [id, value] of Object.entries(prev)) {
        if (ids.has(id)) next[id] = value;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [emails]);

  const [isAdding, setIsAdding] = useState(false);
  const [addValue, setAddValue] = useState("");
  const [addError, setAddError] = useState<string | null>(null);

  const valueFor = (email: EmailAddress) => drafts[email.id] ?? email.email;

  const isDirty = (email: EmailAddress) => {
    const draft = drafts[email.id];
    return draft !== undefined && draft !== email.email;
  };

  const rowError = (email: EmailAddress): string | null => {
    if (!isDirty(email)) return null;
    const parsed = emailSchema.safeParse(drafts[email.id]);
    return parsed.success ? null : "Enter a valid email address";
  };

  const startAdding = () => {
    setIsAdding(true);
    setAddValue("");
    setAddError(null);
  };

  const cancelAdding = useCallback(() => {
    setIsAdding(false);
    setAddValue("");
    setAddError(null);
  }, []);

  const saveAdd = () => {
    const parsed = emailSchema.safeParse(addValue);
    if (!parsed.success) {
      setAddError(parsed.error.issues[0]?.message ?? "Enter a valid email");
      return;
    }
    addEmail.mutate(
      { email: parsed.data, primary: emails.length === 0 },
      { onSuccess: cancelAdding },
    );
  };

  // Persist all dirty, valid rows. Skips invalid rows (their inline error is
  // already shown) and no-ops. Clears each draft once its edit succeeds; a
  // failed edit keeps its draft so the user can correct it. Also commits a
  // pending "Add email" row the user typed but didn't confirm with the check,
  // so hitting the modal's Save no longer silently drops it. Returns the number
  // of edits/adds that failed so the parent can skip a misleading success toast.
  const flushPendingEdits = useCallback(async () => {
    const edits = emails.filter((email) => {
      const draft = drafts[email.id];
      if (draft === undefined) return false;
      const parsed = emailSchema.safeParse(draft);
      return parsed.success && parsed.data !== email.email;
    });

    const results = await Promise.allSettled(
      edits.map(async (email) => {
        const value = emailSchema.parse(drafts[email.id]);
        await updateEmail.mutateAsync({ emailId: email.id, email: value });
        setDrafts((prev) => {
          const next = { ...prev };
          delete next[email.id];
          return next;
        });
      }),
    );

    let failures = results.filter(
      (result) => result.status === "rejected",
    ).length;

    // Commit an in-progress add row (typed but not confirmed via the check).
    if (isAdding && addValue.trim().length > 0) {
      const parsed = emailSchema.safeParse(addValue);
      if (!parsed.success) {
        setAddError(parsed.error.issues[0]?.message ?? "Enter a valid email");
        failures += 1;
      } else {
        try {
          await addEmail.mutateAsync({
            email: parsed.data,
            primary: emails.length === 0,
          });
          cancelAdding();
        } catch {
          // addEmail's onError already surfaced a toast; keep the row open.
          failures += 1;
        }
      }
    }

    return failures;
  }, [emails, drafts, updateEmail, isAdding, addValue, addEmail, cancelAdding]);

  useImperativeHandle(ref, () => ({ flushPendingEdits }), [flushPendingEdits]);

  const syncTooltip =
    "Contact sync is on. Manage this client's emails in your CRM instead.";

  const renderActionButton = (
    button: React.ReactNode,
    disabledTooltip: string,
  ) => {
    if (!isSyncLocked) return button;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex">{button}</div>
        </TooltipTrigger>
        <TooltipContent>{disabledTooltip}</TooltipContent>
      </Tooltip>
    );
  };

  const renderPrimaryTag = () => (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex shrink-0">
          <Tag>Primary</Tag>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{PRIMARY_EMAIL_TOOLTIP}</TooltipContent>
    </Tooltip>
  );

  return (
    <div className="flex flex-1 flex-col gap-2 max-w-[360px] min-w-[250px]">
      <TextLabel>Email</TextLabel>

      <div className="flex flex-col gap-2">
        {(readOnly ? emails : orderedEmails).map((email) => {
          if (readOnly) {
            return (
              <div
                key={email.id}
                data-cy="client-email-row"
                className="flex items-center gap-2"
              >
                <P2 textColor="secondary" className="min-w-0 break-all">
                  {email.email}
                </P2>
                {email.primary && renderPrimaryTag()}
              </div>
            );
          }

          const error = rowError(email);

          return (
            <div
              key={email.id}
              data-cy="client-email-row"
              className="flex items-center gap-2"
            >
              <div className="flex flex-1 flex-col gap-1">
                <Input
                  type="email"
                  value={valueFor(email)}
                  data-cy="client-email-input"
                  disabled={isSyncLocked}
                  onChange={(event) =>
                    setDrafts((prev) => ({
                      ...prev,
                      [email.id]: event.target.value,
                    }))
                  }
                  placeholder="email@example.com"
                />
                {error && (
                  <Text variant="p3" textColor="error">
                    {error}
                  </Text>
                )}
              </div>

              {email.primary ? (
                renderPrimaryTag()
              ) : (
                // Reserve the primary-pill slot so every input stops at the
                // same right edge instead of growing into the empty space.
                <span aria-hidden className="invisible inline-flex shrink-0">
                  <Tag>Primary</Tag>
                </span>
              )}

              {/* Fixed-width action slot: keeps every input's right edge aligned
                  across rows, including the wider "Add email" row (check +
                  cancel). The slot always reserves its width, so a sync-locked
                  primary row (which renders no menu) still lines up. */}
              <div className="flex w-14 shrink-0 items-center justify-end">
                {/* Make-primary is allowed even when sync-locked; delete is not. */}
                {(!email.primary || !isSyncLocked) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="iconSm"
                        variant="ghostSecondary"
                        type="button"
                        aria-label="Email options"
                        data-cy="client-email-menu"
                      >
                        <Icon name="MoreVertIcon" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!email.primary && (
                        <DropdownMenuItem
                          data-cy="client-email-make-primary"
                          disabled={setPrimaryEmail.isPending}
                          onClick={() => setPrimaryEmail.mutate(email.id)}
                        >
                          Make primary
                        </DropdownMenuItem>
                      )}
                      {!isSyncLocked && (
                        <DropdownMenuItem
                          data-cy="client-email-delete"
                          disabled={deleteEmail.isPending}
                          onClick={() => deleteEmail.mutate(email.id)}
                        >
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          );
        })}

        {!readOnly && isAdding && (
          <div className="flex items-center gap-2">
            <div className="flex flex-1 flex-col gap-1">
              <Input
                type="email"
                value={addValue}
                data-cy="client-email-input"
                onChange={(event) => setAddValue(event.target.value)}
                placeholder="email@example.com"
              />
              {addError && (
                <Text variant="p3" textColor="error">
                  {addError}
                </Text>
              )}
            </div>
            {/* Mirror the saved rows' trailing layout (reserved primary slot +
                fixed action slot) so the add-row input lines up with them. */}
            <span aria-hidden className="invisible inline-flex shrink-0">
              <Tag>Primary</Tag>
            </span>
            <div className="flex w-14 shrink-0 items-center justify-end gap-1">
              <Button
                size="iconSm"
                variant="ghost"
                type="button"
                aria-label="Save new email"
                disabled={addEmail.isPending}
                onClick={saveAdd}
              >
                <Icon name="CheckIcon" />
              </Button>
              <Button
                size="iconSm"
                variant="ghostSecondary"
                type="button"
                aria-label="Cancel adding email"
                onClick={cancelAdding}
              >
                <Icon name="CloseIcon" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {!readOnly && !isAdding && emails.length >= MAX_EMAILS && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex w-fit">
              <Button
                size="sm"
                variant="link"
                type="button"
                data-cy="client-email-add"
                disabled
                iconLeft={<Icon name="AddIcon" />}
              >
                Add email
              </Button>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            You can add up to {MAX_EMAILS} email addresses.
          </TooltipContent>
        </Tooltip>
      )}

      {!readOnly &&
        !isAdding &&
        emails.length < MAX_EMAILS &&
        renderActionButton(
          <Button
            size="sm"
            variant="link"
            type="button"
            className="w-fit"
            data-cy="client-email-add"
            disabled={isSyncLocked}
            iconLeft={<Icon name="AddIcon" />}
            onClick={startAdding}
          >
            Add email
          </Button>,
          syncTooltip,
        )}
    </div>
  );
});

ClientEmailList.displayName = "ClientEmailList";
